/* API functions for Berichtswesen (Reporting) */

import { api } from './http';
import { toApiError } from './errors';
import type { BerichtKundeDTO } from './types';

function getBaseURL() {
  const ensureTrailingSlash = (u: string) => (u.endsWith('/') ? u : `${u}/`);
  const raw = (
    (import.meta as any).env?.VITE_API_BASE_URL ||
    (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api')
  );
  const base =
    raw.startsWith('http://') || raw.startsWith('https://')
      ? raw
      : typeof window !== 'undefined'
        ? `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`
        : raw;
  return ensureTrailingSlash(base);
}

function getAccessToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

async function fetchBlob(path: string): Promise<Blob> {
  const url = new URL(path, getBaseURL());
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const resp = await fetch(url.toString(), {
    method: 'POST',
    headers,
  });

  if (!resp.ok) {
    throw new Error(`Fehler beim Erstellen des Berichts (HTTP ${resp.status})`);
  }

  return resp.blob();
}

async function fetchBlobGet(path: string): Promise<Blob> {
  const url = new URL(path, getBaseURL());
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const resp = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!resp.ok) {
    throw new Error(`Fehler beim Download des Berichts (HTTP ${resp.status})`);
  }

  return resp.blob();
}

export async function getBerichtsjahr(): Promise<number> {
  try {
    const { data } = await api.get<{ berichtsjahr: number }>('Berichtswesen/berichtsjahr');
    return data.berichtsjahr;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function setBerichtsjahr(jahr: number): Promise<number> {
  try {
    const { data } = await api.put<{ berichtsjahr: number }>('Berichtswesen/berichtsjahr', { berichtsjahr: jahr });
    return data.berichtsjahr;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getBenchmarkingKunden(): Promise<BerichtKundeDTO[]> {
  try {
    const { data } = await api.get<BerichtKundeDTO[]>('Berichtswesen/kunden');
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function generateBerichtForKunde(ik: string): Promise<void> {
  try {
    await api.post(`Berichtswesen/generate/${ik}`);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function downloadExcel(ik: string): Promise<Blob> {
  return fetchBlobGet(`Berichtswesen/download/${ik}/excel`);
}

export async function downloadPdf(ik: string): Promise<Blob> {
  return fetchBlobGet(`Berichtswesen/download/${ik}/pdf`);
}

export async function downloadAllExcelZip(): Promise<Blob> {
  return fetchBlob('Berichtswesen/generate-all/excel');
}

export async function downloadAllPdfZip(): Promise<Blob> {
  return fetchBlob('Berichtswesen/generate-all/pdf');
}
